// ABOUTME: React application entry point — mounts the root with all providers.
// ABOUTME: Provider order: Sentry.ErrorBoundary → ClerkProvider → AnalyticsProvider → ThemeProvider → RouterProvider (outermost to innermost).
import * as Sentry from '@sentry/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ClerkProvider } from '@clerk/react';
import { RouterProvider } from 'react-router';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { theme } from './theme';
import { router } from './router';
import { AnalyticsProvider } from './analytics/AnalyticsProvider';
import './index.css';

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  beforeSend(event) {
    const message = event.exception?.values?.[0]?.value ?? ''
    if (message.startsWith('ClerkJS: Network error')) return null
    if (message.startsWith('Error invoking postMessage: Java object is gone')) return null
    if (message.startsWith('zaloJSV2 is not defined')) return null
    return event
  },
});

const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!publishableKey) {
  throw new Error('Missing VITE_CLERK_PUBLISHABLE_KEY in environment');
}

const clerkAppearance = {
  variables: {
    colorPrimary: '#F97316',
    colorBackground: '#FFF7ED',
    colorForeground: '#1C1917',
    fontFamily: '"Poppins", system-ui, sans-serif',
    borderRadius: '0.5rem',
  },
};

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<p>Something went wrong. Please refresh the page.</p>}>
      <ClerkProvider publishableKey={publishableKey} appearance={clerkAppearance}>
        <AnalyticsProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <RouterProvider router={router} />
          </ThemeProvider>
        </AnalyticsProvider>
      </ClerkProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
